import { useEffect, useState } from 'react';
import { IconShoe } from '@runner/ui';
import { useLaunchInfo } from '../data/launch';
import { countdownText, DEFAULT_RACE_MINUTES, halloweenEvent, runDayPhase, runDayStarts, usdFromUnits, type RunDayEvent } from '../data/runDay';
import type { WorldView } from '../data/types';
import { racePresentation } from '../data/racePresentation';
import { units } from '../lib/format';
import { RaceRules } from './RaceRules';

/** Next scheduled race start (UTC "HH:MM" slots) after `now` (ms). */
export function nextRace(times: string[], now: number, from = 0): number | null {
  const day = Date.UTC(new Date(now).getUTCFullYear(), new Date(now).getUTCMonth(), new Date(now).getUTCDate());
  const slots = times
    .map((t) => t.split(':').map(Number))
    .filter(([h, m]) => Number.isFinite(h) && Number.isFinite(m))
    .flatMap(([h, m]) => [day + (h * 60 + m) * 60_000, day + 86_400_000 + (h * 60 + m) * 60_000])
    .filter((t) => t > now && t >= from)
    .sort((x, y) => x - y);
  return slots[0] ?? null;
}

/** Golden Shoe in USD: the contract opens min(shoe size, prize reserve) each race; the team adds `shoeBonus` for the winner. */
export function goldenShoeUsd(view: WorldView, races: { shoe?: number; shoeBonus?: number }): number | null {
  const reserve = view.treasury ? Math.floor(Number(BigInt(view.treasury.buckets.prizeReserve)) / 10 ** view.quote.decimals) : null;
  const onChain = reserve !== null ? (races.shoe ? Math.min(races.shoe, reserve) : reserve) : (races.shoe ?? null);
  return onChain !== null ? onChain + (races.shoeBonus ?? 0) : null;
}

export const hms = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Season race schedule: a countdown to the next main-event race (from /launch.json "races"),
 * the onchain Golden Shoe with separate team-draw disclosures. Shows "race live" while a run is active.
 */
export function RaceChip({ view }: { view: WorldView }) {
  const launch = useLaunchInfo();
  const races = launch?.races;
  const event = halloweenEvent(launch);
  const [now, setNow] = useState(() => Date.now());
  const [rules, setRules] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (event) return <RunDayChip view={view} event={event} now={now} rules={rules} setRules={setRules} />;
  if (!races?.times.length) return null;
  const state = racePresentation(view);
  const live = ['live', 'resolving', 'starting'].includes(state.kind);
  // Before the season's first race, count down to it (not to an earlier slot of the same schedule).
  const from = races.from ? Date.parse(races.from) : 0;
  const next = nextRace(races.times, now, Number.isFinite(from) ? from : 0) ?? (from > now ? from : null);
  const shoe = goldenShoeUsd(view, races);
  // Ticket window (contract rule): entries open when the race's shoe opens at race start and close at its cutoff,
  // before the runner can first reach the shoe. Only buys through runner.party in that window count.
  const run = view.run?.shoe;
  const chainNow = view.timing.now;
  const ticketsOpen = !view.freshness.stale && live && run?.status === 'Open' && chainNow < run.cutoff;
  const left = run ? Math.max(0, run.cutoff - chainNow) : 0;
  // The Any-App draw counts net buys on any app from anyAppEarlyHours before the race until it ends.
  const earlyOpen = state.kind !== 'offline' && !live && !!races.anyAppPrize && next !== null && next - now <= (races.anyAppEarlyHours ?? 2) * 3_600_000;
  const tickets = state.kind === 'offline' ? 'Entry status unavailable until live data returns.' : run?.winnerLabel
    ? `Won by ${run.winnerLabel}!`
    : ticketsOpen
      ? `Tickets open · close in ${Math.floor(left / 60)}:${String(Math.floor(left % 60)).padStart(2, '0')}${run && run.yourWeight > 0n ? ' · ✓ you’re in' : ''}`
      : live && run
        ? 'Tickets closed · steer me into the shoe!'
        : earlyOpen
          ? `$${races.anyAppPrize} draw is OPEN: buy now on any app · shoe tickets open at race start`
          : `Shoe tickets open at race start${races.anyAppPrize ? ` · $${races.anyAppPrize} draw opens ${races.anyAppEarlyHours ?? 2}h before` : ''}`;
  const stateLabel = state.kind === 'offline' ? 'Status unavailable' : live
    ? view.phase === 'funding' ? 'Preparing the run' : view.phase === 'preview' ? 'Starting soon' : 'Race live'
    : `Next race ${next ? countdownText(next - now) : '--:--:--'}`;
  // Between races the last run's (settled) shoe is history: show the next shoe instead.
  const curShoe = live ? view.run?.shoe : undefined;
  return (
    <div className="race-hud">
      <details className="race-chip" data-testid="race-chip" data-live={live ? 'yes' : 'no'} onKeyDown={(e) => {
        if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); }
      }}>
        <summary aria-label="Race prizes and entry details">
          <span className="race-shoe-art" aria-hidden="true"><IconShoe size={32} /></span>
          <span className="race-headline">
            <span className="race-label">{curShoe ? 'Golden Shoe' : 'Next Golden Shoe'} <span aria-hidden="true">↗</span></span>
            <strong>{curShoe ? `${units(curShoe.amount, view.quote.decimals, 2)} ${view.quote.symbol}` : shoe !== null ? `${Math.max(0, shoe - (races.shoeBonus ?? 0))} ${view.quote.symbol}` : 'Amount pending'}{races.shoeBonus ? <em className="race-bonus"> + {races.shoeBonus} team bonus</em> : null}</strong>
            <span className="race-clock">{stateLabel}</span>{!live && next && <span className="race-clock">{new Intl.DateTimeFormat(undefined, { ...(next - now > 20 * 3_600_000 ? { weekday: 'short' as const } : {}), hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(next)}</span>}
          </span>
        </summary>
        <div className="race-expanded">
          {next && <p>Next scheduled race: <strong>{new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(next)}</strong></p>}
          <p>Onchain Shoe amount shown above. {races.shoeBonus ? `The team advertises a separate $${races.shoeBonus} bonus for the winner; funding and payment are not reported by this app.` : ''}</p>
          <p className="race-tickets" data-testid="race-tickets" data-open={ticketsOpen || earlyOpen ? 'yes' : 'no'}>{tickets}</p>
          {races.anyAppPrize ? <p><strong>${races.anyAppPrize}</strong> Any-App Prize</p> : null}
          <p className="race-meta">{races.label ?? 'Season 1'} · {races.display ?? `${races.times.join(' & ')} UTC`}{races.prize ? ` · ${races.prize}` : ''}</p>
          <button type="button" className="race-rules-link" onClick={() => setRules(true)} data-testid="race-rules-open">How races work</button>
        </div>
      </details>
      {ticketsOpen && <span className="race-entry-status">Entries open · {Math.floor(left / 60)}:{String(Math.floor(left % 60)).padStart(2, '0')}</span>}
      {rules && <RaceRules view={view} shoe={curShoe ? Number(curShoe.amount) / 10 ** view.quote.decimals : shoe !== null ? Math.max(0, shoe - (races.shoeBonus ?? 0)) : null} anyApp={races.anyAppPrize} earlyHours={races.anyAppEarlyHours ?? 2} schedule={races.display ?? `${races.times.join(' & ')} UTC`} onClose={() => setRules(false)} />}
    </div>
  );
}

/**
 * The race bar under the RUN DAY flag: counts down to the event's six slots and shows the whole prize reserve as a jackpot
 * that rolls over (no per-race shoe cap, no Any-App draw). Ticket state still comes from the onchain shoe.
 */
function RunDayChip({ view, event, now, rules, setRules }: { view: WorldView; event: RunDayEvent; now: number; rules: boolean; setRules(v: boolean): void }) {
  const starts = runDayStarts(event);
  const phase = runDayPhase(starts, now, event.raceMinutes ?? DEFAULT_RACE_MINUTES);
  const state = racePresentation(view);
  const live = ['live', 'resolving', 'starting'].includes(state.kind);
  const name = event.name ?? 'RUN DAY';
  const next = phase.kind === 'before' || phase.kind === 'between' ? phase.next : phase.kind === 'live' ? phase.next : null;
  const run = view.run?.shoe;
  const chainNow = view.timing.now;
  const ticketsOpen = !view.freshness.stale && live && run?.status === 'Open' && chainNow < run.cutoff;
  const left = run ? Math.max(0, run.cutoff - chainNow) : 0;
  const jackpot = live && run && run.status !== 'None' ? usdFromUnits(run.amount, view.quote.decimals) : usdFromUnits(view.treasury?.buckets.prizeReserve, view.quote.decimals);
  const amount = jackpot !== null ? `${jackpot.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${view.quote.symbol}` : 'Amount pending';
  const tickets = state.kind === 'offline' ? 'Entry status unavailable until live data returns.' : run?.winnerLabel && live
    ? `Caught by ${run.winnerLabel}!`
    : ticketsOpen
      ? `Tickets open · close in ${Math.floor(left / 60)}:${String(Math.floor(left % 60)).padStart(2, '0')}${run && run.yourWeight > 0n ? ' · ✓ you’re in' : ''}`
      : live && run
        ? 'Tickets closed · steer me into the shoe!'
        : phase.kind === 'done' ? `${name} is over. The jackpot rolls over.` : 'Jackpot tickets open when each race starts. Buy RUNNER on runner.party.';
  // The schedule only names the race; live/finished state is the chain's (a race can start or run late).
  const raceNo = phase.kind === 'live' ? phase.index + 1 : null;
  const upcoming = phase.kind === 'before' || phase.kind === 'between' ? phase.index + 1 : null;
  const stateLabel = state.kind === 'offline' ? 'Status unavailable' : live
    ? view.phase === 'funding' ? 'Preparing the run' : view.phase === 'preview' ? 'Starting soon' : (raceNo ? `Race ${raceNo} live` : 'Race live')
    : phase.kind === 'done' ? `${name} is over`
      : !next ? 'The Witching Hour'
        : `${phase.kind === 'before' ? 'First race' : `Race ${upcoming ?? (raceNo ?? 0) + 1} of ${starts.length}`} in ${countdownText(next - now)}`;
  const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return (
    <div className="race-hud">
      <details className="race-chip" data-testid="race-chip" data-live={live ? 'yes' : 'no'} data-event="halloween" onKeyDown={(e) => {
        if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); }
      }}>
        <summary aria-label="Jackpot and entry details">
          <span className="race-shoe-art" aria-hidden="true"><IconShoe size={32} /></span>
          <span className="race-headline">
            <span className="race-label">Golden Shoe jackpot <span aria-hidden="true">↗</span></span>
            <strong>{amount}</strong>
            <span className="race-clock">{stateLabel}</span>{!live && next && <span className="race-clock">{fmt.format(next)}</span>}
          </span>
        </summary>
        <div className="race-expanded">
          {next && <p>Next race: <strong>{fmt.format(next)}</strong></p>}
          <p>The jackpot is the onchain prize reserve. It rolls over until someone catches it.</p>
          <p className="race-tickets" data-testid="race-tickets" data-open={ticketsOpen ? 'yes' : 'no'}>{tickets}</p>
          <p className="race-meta">{name} · {starts.length} races · {event.times.join(', ')} UTC</p>
          <button type="button" className="race-rules-link" onClick={() => setRules(true)} data-testid="race-rules-open">How races work</button>
        </div>
      </details>
      {ticketsOpen && <span className="race-entry-status">Entries open · {Math.floor(left / 60)}:{String(Math.floor(left % 60)).padStart(2, '0')}</span>}
      {rules && <RaceRules view={view} shoe={jackpot} earlyHours={0} schedule={`${event.times.join(', ')} UTC`} event={event} onClose={() => setRules(false)} />}
    </div>
  );
}
